import { useRef } from "react";
import { useForm } from "react-hook-form";
import { BsPencilSquare } from "react-icons/bs";

import DialogModal from "../../../DialogModal";

import OverlayTooltip from "../../../OverlayTooltip";

import { cn } from "../../../../helpers/cn";

import {
  returnPermissionOptions,
  returnPermissionKeys,
  useUpdateCustomerReturnPermission,
} from "../returnPermission.util";

const formID = "edit-selected-permission-form";

/**
 * @typedef {Object} Customer
 * @property {string} customerId
 * @property {string} customerName
 * @property {string} customerAddress
 * @property {string} customerCity
 * @property {string} customerState
 * @property {string} customerZip
 * @property {string} reasonEntered
 * @property {string} enteredBy
 * @property {string} enteredDate
 * @property {string} permission
 */

/**
 * @typedef {Object} props
 * @property {Object} instance
 * @property {Object} instance.row
 * @property {Customer} instance.row.original
 * @property {Array.<Object>} instance.selectedFlatRows
/**
 * @param {props} props
 */

function EditSelectedPermission({ instance }) {
  /**
   * @type {React.MutableRefObject<{show: function():void, hide: function():void} | null>} dialogModalRef
   */
  const dialogModalRef = useRef(null);

  const { isLoading, mutateBatchUpdate } = useUpdateCustomerReturnPermission();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues: {
      permission: "",
    },
  });

  const { selectedFlatRows } = instance;

  const onCancel = () => {
    reset();
    dialogModalRef.current?.hide();
  };

  const onSubmit = (submitData) => {
    // If there are no selected rows, return

    if (selectedFlatRows.length === 0) {
      onCancel();
      return;
    }

    const getFormattedData = (permission) =>
      selectedFlatRows.map((row) => {
        const original = row.original;

        return {
          ClientId: original.clientId,
          CustomerId: original.customerId,
          ...permission,
        };
      });

    if (submitData.permission === returnPermissionKeys.allowedReturns) {
      const formatRequestData = getFormattedData({
        ByApproval: false,
        isActive: false,
      });

      mutateBatchUpdate(formatRequestData);
    }

    if (submitData.permission === returnPermissionKeys.notAllowedReturns) {
      const formatRequestData = getFormattedData({
        ByApproval: false,
        isActive: true,
      });

      mutateBatchUpdate(formatRequestData);
    }

    if (
      submitData.permission === returnPermissionKeys.allowedReturnByApproval
    ) {
      const formatRequestData = getFormattedData({
        ByApproval: true,
        isActive: true,
      });

      mutateBatchUpdate(formatRequestData);
    }

    reset();
    dialogModalRef.current?.hide();
  };

  return (
    <div>
      <OverlayTooltip label={isLoading ? "Updating..." : "Edit Selected"}>
        <button
          type="button"
          className={cn(
            "btn tw-border-0 tw-outline-none tw-grid tw-place-content-center hover:tw-bg-gray-200 tw-px-2",
            isLoading ? "tw-cursor-not-allowed tw-bg-gray-200" : ""
          )}
          onClick={() => {
            dialogModalRef.current?.show();
          }}
          disabled={isLoading || selectedFlatRows.length === 0}
        >
          {/* Edit Selected */}
          <BsPencilSquare className="tw-h-5 tw-w-5" />
        </button>
      </OverlayTooltip>

      <DialogModal
        title="Edit Selected Permissions"
        ref={dialogModalRef}
        cancelButtonProps={{ onClick: onCancel }}
        saveButtonProps={{ form: formID, type: "submit", onClick: undefined }}
        onHide={onCancel}
      >
        <form id={formID} onSubmit={handleSubmit(onSubmit)}>
          <select
            className={`form-control ${
              errors.permission
                ? "tw-border-red-500 focus:tw-border-red-500"
                : ""
            }`}
            {...register("permission", { required: true })}
          >
            <option value="">Select Permission</option>
            {returnPermissionOptions.map((option) => (
              <option key={option.value} value={option.label}>
                {option.label}
              </option>
            ))}
          </select>
          {errors.permission && (
            <small className="tw-text-red-500 tw-m-0">Required</small>
          )}
        </form>
      </DialogModal>
    </div>
  );
}

export default EditSelectedPermission;
